import { Component, computed, input, model } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { fromDateInputValue, toDateInputValue } from 'shared-lib';
import { provideGermanDateAdapter } from '../../parking-space/parking-space-process-details/german-date-adapter';

/**
 * A date field that always reads and writes dd.mm.yyyy, for the hotline forms.
 *
 * Replaces `<input type="date">`, whose display format follows the browser's UI language and not
 * the app's: an operator on an English Chrome saw mm/dd/yyyy on an Austrian contract form, and no
 * attribute can change that. The value stays the `YYYY-MM-DD` string the forms already validate and
 * send through `dateInputValueToIso`, so callers only swap the element.
 *
 * Text that does not parse as a date is cleared on commit rather than kept on screen: an optional
 * field would otherwise show a date the form is not actually sending.
 */
@Component({
  selector: 'app-date-field',
  standalone: true,
  imports: [NgClass, MatDatepickerModule, MatIconModule, TranslatePipe],
  providers: [provideGermanDateAdapter()],
  template: `
    <div class="relative">
      <input [id]="inputId()" [matDatepicker]="picker" [value]="date()" [min]="minDate()"
        (dateChange)="onDateChange($event)" [placeholder]="'common.datePlaceholder' | translate"
        autocomplete="off"
        [ngClass]="invalid() ? 'border-red-400' : 'border-slate-200'"
        class="w-full border rounded-lg py-1.5 pl-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <button type="button" (click)="picker.open()" tabindex="-1"
        class="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center text-slate-400 hover:text-blue-600 transition">
        <mat-icon class="text-[18px] w-[18px] h-[18px] leading-none">calendar_today</mat-icon>
      </button>
      <mat-datepicker #picker></mat-datepicker>
    </div>
  `,
})
export class DateFieldComponent {
  /** The date as `YYYY-MM-DD`, or `''` when empty. Two-way bindable: `[(value)]="validFrom"`. */
  value = model<string>('');
  /** Earliest selectable date as `YYYY-MM-DD`; the form still validates it on its own. */
  min = input<string | null>(null);
  inputId = input<string | null>(null);
  /** Red border, driven by the form's own validation. */
  invalid = input(false);

  protected readonly date = computed(() => fromDateInputValue(this.value()));
  protected readonly minDate = computed(() => fromDateInputValue(this.min()));

  protected onDateChange(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    if (date && !Number.isNaN(date.getTime())) {
      this.value.set(toDateInputValue(date));
      return;
    }
    // Unparsable or empty: show and send nothing, never a half-typed date.
    (event.targetElement as HTMLInputElement).value = '';
    this.value.set('');
  }
}
